import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { match } from 'ts-pattern';

import type {
    AddLicenseInput,
    GetPracticeMemberInsightsOutput,
    GetPracticeMemberPreferencesOutput,
    OnboardingStep,
    PracticeMember,
    PracticeMemberUpdateWelcomeGuideInput,
    UpdateInsightsInput,
    UpdateLicenseInput,
    UpdatePracticeMemberInput,
} from '@/entities/practice-member';

import { practiceMemberApi } from './practice-member.api';
import { queryKeys } from './practice-member.queries';

export const mutationKeys = {
    all: ['practice-members'] as const,
    updateWelcomeGuide: () => [...mutationKeys.all, 'update-welcome-guide'] as const,
    dismissGuidance: () => [...mutationKeys.all, 'dismiss-guidance'] as const,
    addLicense: () => [...mutationKeys.all, 'add-license'] as const,
    updateLicense: () => [...mutationKeys.all, 'update-license'] as const,
    removeLicense: () => [...mutationKeys.all, 'remove-license'] as const,
};

export function useUpdateWelcomeGuide() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.updateWelcomeGuide(),
        mutationFn: (input: PracticeMemberUpdateWelcomeGuideInput) =>
            practiceMemberApi.updateWelcomeGuide(input),
        onMutate: async (input) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.preferences() });

            const prevData =
                queryClient.getQueryData<GetPracticeMemberPreferencesOutput>(
                    queryKeys.preferences(),
                ) || ({} as GetPracticeMemberPreferencesOutput);

            queryClient.setQueryData(queryKeys.preferences(), {
                ...prevData,
                'welcome-guide': {
                    ...prevData['welcome-guide'],
                    ...input,
                },
            });

            return { prevData };
        },
        // Deliberately no success invalidation: the optimistic write above is already the
        // canonical value, and the preferences read is strongly consistent, so the next mount
        // reconciles without an extra round trip here.
        onError: (_, __, context) => {
            queryClient.setQueryData(queryKeys.preferences(), context?.prevData);
        },
    });
}

export function useUpdatePracticeMemberOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (command: UpdatePracticeMemberInput) => practiceMemberApi.update(command),
        onMutate: (newData) => {
            const previousPracticeMember = queryClient.getQueryData<PracticeMember>(queryKeys.me());

            if (previousPracticeMember) {
                queryClient.setQueryData<PracticeMember>(queryKeys.me(), {
                    ...previousPracticeMember,
                    ...newData,
                });
            }

            return { previousPracticeMember };
        },
        onError: (_, __, context) => {
            queryClient.setQueryData(queryKeys.me(), () => context?.previousPracticeMember);
        },
    });
}

export function useUpdateOnboardingStepOptions() {
    return mutationOptions({
        mutationFn: (command: OnboardingStep) => practiceMemberApi.updateOnboardingStep(command),
    });
}

export function useDeleteOnboardingStepOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: () => practiceMemberApi.deleteOnboardingStep(),
        onSuccess: () => queryClient.refetchQueries({ queryKey: queryKeys.all }),
    });
}

export function useDismissGuidanceOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.dismissGuidance(),
        mutationFn: (key: string) => practiceMemberApi.dismissGuidance(key),
        onMutate: async (key) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.preferences() });

            const prevData =
                queryClient.getQueryData<GetPracticeMemberPreferencesOutput>(
                    queryKeys.preferences(),
                ) || ({} as GetPracticeMemberPreferencesOutput);

            queryClient.setQueryData<GetPracticeMemberPreferencesOutput>(queryKeys.preferences(), {
                ...prevData,
                guidance: {
                    ...prevData.guidance,
                    keys: (prevData.guidance?.keys ?? []).filter((k) => k !== key),
                },
            });

            return { prevData };
        },
        onError: (_error, _key, context) => {
            queryClient.setQueryData(queryKeys.preferences(), context?.prevData);
        },
    });
}

// None of the three writes optimistically: the server mints the id, so there is nothing to merge.
export function useAddLicenseOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.addLicense(),
        mutationFn: (data: AddLicenseInput) => practiceMemberApi.addLicense(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.licenses() }),
        onError: (error) => {
            console.error('Failed to add license:', error);
            toast.error('Failed to add license');
        },
    });
}

export function useUpdateLicenseOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.updateLicense(),
        mutationFn: ({ licenseId, ...data }: UpdateLicenseInput & { licenseId: string }) =>
            practiceMemberApi.updateLicense(licenseId, data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.licenses() }),
        onError: (error) => {
            console.error('Failed to save license:', error);
            toast.error('Failed to save license');
        },
    });
}

export function useRemoveLicenseOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.removeLicense(),
        mutationFn: (licenseId: string) => practiceMemberApi.removeLicense(licenseId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.licenses() }),
        onError: (error) => {
            console.error('Failed to delete license:', error);
            toast.error('Failed to delete license');
        },
    });
}

export function useUpdateInsightsOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (data: UpdateInsightsInput) => practiceMemberApi.updateInsights(data),
        onSuccess: (_, newData) => {
            const previousInsights = queryClient.getQueryData<GetPracticeMemberInsightsOutput>(
                queryKeys.insights(),
            );

            if (previousInsights) {
                queryClient.setQueryData(queryKeys.insights(), {
                    ...previousInsights,
                    [newData.type]: match(newData)
                        .with({ type: 'why' }, ({ customValue }) => ({
                            ...(customValue !== undefined && { customValue }),
                        }))
                        .with({ type: 'ehr' }, ({ selection, customValue }) => ({
                            selection,
                            ...(selection === 'OTHER' &&
                                customValue !== undefined && { customValue }),
                        }))
                        .exhaustive(),
                });
            }
        },
    });
}
